/**
 * Floating @mention typeahead list (Ditto-style).
 */

import { useEffect, useRef } from 'react';
import { Radio, User } from 'lucide-react';

import {
  isNetworkSearchCandidate,
  type MentionCandidate,
} from '@/lib/nostr/mentions';
import { cn } from '@/lib/utils';

export function MentionTypeahead({
  open,
  candidates,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  onClose,
  loading,
  query,
  anchorRect,
}: {
  open: boolean;
  candidates: MentionCandidate[];
  activeIndex: number;
  onActiveIndexChange: (i: number) => void;
  onSelect: (c: MentionCandidate) => void;
  onClose: () => void;
  loading?: boolean;
  query: string;
  /** Viewport rect near caret / textarea */
  anchorRect: { top: number; left: number; bottom: number } | null;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open || !anchorRect) return null;

  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const openUp = spaceBelow < 220 && anchorRect.top > 220;

  return (
    <div
      ref={listRef}
      className="fixed z-[80] w-[min(320px,calc(100vw-24px))] max-h-[240px] overflow-y-auto rounded-xl border border-[#2a2a2a] bg-[#141414] shadow-2xl shadow-black/50 scrollbar-thin"
      style={{
        left: Math.min(anchorRect.left, window.innerWidth - 340),
        ...(openUp
          ? { bottom: window.innerHeight - anchorRect.top + 6 }
          : { top: anchorRect.bottom + 6 }),
      }}
      role="listbox"
      aria-label="Mention someone"
    >
      <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-[#3a3a3a] border-b border-[#1f1f1f]">
        {query ? `Matching “${query}”` : 'Mention · @nostr searches your follows'}
      </div>

      {loading && candidates.length === 0 && (
        <div className="px-3 py-4 text-[12px] text-[#5a5a5a]">Loading follows…</div>
      )}

      {!loading && candidates.length === 0 && (
        <div className="px-3 py-4 text-[12px] text-[#5a5a5a] leading-relaxed">
          No matches in your follows.
          {query.includes('@') || query.includes('.')
            ? ' Try a full NIP-05 like name@domain.'
            : ' Paste an npub, or type name@domain.'}
        </div>
      )}

      {candidates.map((c, i) => {
        const isNet = isNetworkSearchCandidate(c);
        return (
        <button
          key={`${c.pubkey}-${c.source}`}
          type="button"
          data-idx={i}
          role="option"
          aria-selected={i === activeIndex}
          onMouseEnter={() => onActiveIndexChange(i)}
          onClick={() => onSelect(c)}
          className={cn(
            'w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors',
            i === activeIndex ? 'bg-[#1c1c1c]' : 'hover:bg-[#181818]',
          )}
        >
          {isNet ? (
            <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-amber-200/80 shrink-0 border border-amber-900/40">
              <Radio className="w-3.5 h-3.5" />
            </div>
          ) : c.picture ? (
            <img
              src={c.picture}
              alt=""
              className="w-8 h-8 rounded-full object-cover shrink-0 bg-[#1a1a1a]"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#5a5a5a] shrink-0">
              <User className="w-3.5 h-3.5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] text-[#e8e6e3] truncate font-medium">
                {isNet
                  ? '@nostr'
                  : c.source === 'self'
                    ? `${c.displayName} (you)`
                    : c.displayName}
              </span>
              {isNet && (
                <span className="text-[9px] uppercase tracking-wide text-amber-200/50 shrink-0">
                  search
                </span>
              )}
              {c.source === 'follow' && (
                <span className="text-[9px] uppercase tracking-wide text-[#3a3a3a] shrink-0">
                  following
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#5a5a5a] truncate">
              {isNet
                ? 'Search recent notes from people you follow'
                : c.nip05 || c.npub.slice(0, 16) + '…'}
            </div>
          </div>
        </button>
        );
      })}
    </div>
  );
}
